import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColocationMap } from '@/components/design-system/atoms/map';
import { Slide } from '../image-gallery/types';
import NavigationTabs from './NavigationTabs';
import MARKER_HTML from '@/components/design-system/atoms/map/Marker';
import NavButton from '@/components/design-system/atoms/nav-button';
import { composeClassName } from '@/utils/util_helpers/class_names';

interface SliderViewProps {
  slides: Slide[];
  sliderRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  selectedTabIndex: number;
  onCloseSlider: () => void;
  onPrevSlide: () => void;
  onNextSlide: () => void;
  onTabSelect: (index: number) => void;
  virtualTourAvailable: boolean;
  houseData: {
    location: {
      latitude: number;
      longitude: number;
    };
    address: string;
  };
  placeholderImage: string;
  fullscreen?: boolean;
}

const SliderView: React.FC<SliderViewProps> = ({
  slides,
  sliderRef,
  isMobile,
  selectedTabIndex,
  onCloseSlider,
  onPrevSlide,
  onNextSlide,
  onTabSelect,
  virtualTourAvailable,
  houseData,
  placeholderImage,
  fullscreen = false
}) => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile || !fullscreen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isNavButton = target.closest('button[type="button"]');

      if (imageRef.current &&
          !imageRef.current.contains(target) &&
          !isNavButton &&
          containerRef.current?.contains(target)) {
        onCloseSlider();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        onPrevSlide();
      } else if (event.key === 'ArrowRight') {
        onNextSlide();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullscreen, onCloseSlider, isMobile, onPrevSlide, onNextSlide]);

  const markers = [
    {
      lat: houseData.location.latitude,
      lng: houseData.location.longitude,
      content: MARKER_HTML,
    },
  ];

  return (
    <div
      className={
        fullscreen && !isMobile
          ? 'fixed inset-0 z-50 flex h-screen w-screen flex-col bg-transparent'
          : 'relative mx-auto w-full max-w-[130rem]'
      }
    >
      {/* Navigation Arrows and Main Slider */}
      <div
        ref={containerRef}
        className={composeClassName(
          'relative flex w-full items-center justify-center',
          fullscreen ? 'h-full' : '',
          !isMobile && fullscreen ? 'cursor-zoom-out' : ''
        )}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onCloseSlider();
          }
        }}
      >
        {!isMobile && <NavButton direction="left" onClick={onPrevSlide} />}

        {/* Main Slider */}
        <div
          className={composeClassName(
            'w-full',
            isMobile ? 'px-0' : 'p-0',
            fullscreen ? 'h-full' : ''
          )}
        >
          <div
            ref={sliderRef}
            className={composeClassName('w-full overflow-hidden', fullscreen ? 'h-full' : '')}
            style={!fullscreen ? { height: '500px' } : undefined}
          >
            {slides.map((slide, index) => (
              <div
                key={`slide-${index}`}
                className={composeClassName('carousel-cell w-full', fullscreen ? 'h-full' : '')}
                style={!fullscreen ? { height: '500px' } : undefined}
              >
                {slide.type === 'image' ? (
                  <div
                    ref={!isMobile ? imageRef : null}
                    className="relative flex h-full w-full items-center justify-center"
                  >
                    <div
                      className={composeClassName(
                        'relative h-full w-full flex items-center justify-center',
                        !isMobile && fullscreen ? 'cursor-zoom-out' : ''
                      )}
                      onClick={(e) => {
                        if (e.target === e.currentTarget) {
                          onCloseSlider();
                        }
                      }}
                    >
                      <img
                        src={slide.src}
                        alt={slide.alt}
                        className={composeClassName(
                          isMobile ? 'h-full w-full object-cover' : 'max-h-full max-w-full',
                          fullscreen && !isMobile ? 'cursor-default object-contain max-h-[calc(100vh-80px)] w-auto max-w-full mx-auto block' : ''
                        )}
                        onClick={(e) => e.stopPropagation()}
                        onError={(e) => {
                          e.currentTarget.src = placeholderImage;
                        }}
                      />
                    </div>
                  </div>
                ) : slide.type === 'map' ? (
                  <div className="h-full w-full">
                    <ColocationMap
                      id="house_location_map"
                      initialCoordinates={{
                        lat: houseData.location.latitude,
                        lng: houseData.location.longitude,
                      }}
                      markers={markers}
                      height="100%"
                      className="h-full w-full rounded-lg shadow-sm"
                      address={houseData.address}
                      DisplayHeading={false}
                    />
                  </div>
                ) : (
                  <iframe
                    src={`${slide.url}&play=1&help=1&hr=0&hl=1&gt=1&wh=0&vr=0&mls=1`}
                    frameBorder="0"
                    className="h-full w-full"
                    allowFullScreen
                    data-tour-id="virtual-tour"
                    data-autoplay="true"
                    data-help="true"
                    data-hide-controls="false"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {!isMobile && <NavButton direction="right" onClick={onNextSlide} />}
      </div>

      <NavigationTabs
        virtualTourAvailable={virtualTourAvailable}
        selectedTabIndex={selectedTabIndex}
        slidesLength={slides.length}
        onTabSelect={onTabSelect}
        isMobile={isMobile}
      />
    </div>
  );
};

export default SliderView;
